import React, { useMemo, useState } from 'react';
import { normalizeChannelHandle } from '@jwp/ott-common/src/utils/channelHandle';
import type { Channel } from '@jwp/ott-common/src/graphql/query/channel.queries';
import { copyToClipboard } from '../../utils/clipboard';

import styles from './ChannelAboutModal.module.scss';

type Props = {
  channel: Channel;
  contactEmail?: string;
  followingCount?: number;
  followersCount?: number;
  videosCount?: number;
  viewsCount?: number;
  isReported?: boolean;
};

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 15H4.5C3.39543 15 2.5 14.1046 2.5 13V4.5C2.5 3.39543 3.39543 2.5 4.5 2.5H13C14.1046 2.5 15 3.39543 15 4.5V5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const ChannelAboutModal = ({
  channel,
  contactEmail = '',
  followingCount = 0,
  followersCount = 0,
  videosCount = 0,
  viewsCount = 0,
  isReported
}: Props) => {
  const [copied, setCopied] = useState(false);
  const handle = normalizeChannelHandle(channel.chHandle);

  const joinDate = useMemo(() => {
    const date = new Date(channel.chCreatedAt);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, [channel.chCreatedAt]);

  const channelUrl = `${window.location.origin}/@${handle}`;
  const shareUrl = `${window.location.origin}/m/channel/${channel.chId}`;
  const viewsLabel = viewsCount.toLocaleString('en-US');

  const onCopy = () => {
    copyToClipboard(shareUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{channel.chName}</h2>
      <div className={styles.meta}>
        @{handle}
        {/* <span className={styles.metaSep}>|</span>
        {followingCount} Following
        <span className={styles.metaSep}>|</span>
        {followersCount} Followers */}
        <span className={styles.metaSep}>|</span>
        {videosCount} Videos
      </div>

      {channel.chDescription ? <p className={styles.description}>{channel.chDescription}</p> : null}

      <div className={styles.group}>
        <div className={styles.label}>Contact</div>
        <div className={styles.value}>{contactEmail || '—'}</div>
      </div>

      <div className={styles.group}>
        <div className={styles.label}>Channel URL</div>
        <div className={styles.urlRow}>
          <span className={`${styles.value} ${styles.noCopy}`}>
            {channelUrl}
          </span>
          <button
            type="button"
            className={styles.copyButton}
            onClick={onCopy}
            aria-label={copied ? 'Copied' : 'Copy channel URL'}
            title={copied ? 'Copied' : 'Copy'}
            disabled={isReported}
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      <div className={styles.group}>
        <div className={styles.label}>Join date</div>
        <div className={styles.value}>{joinDate}</div>
      </div>

      {/* <div className={styles.group}>
        <div className={styles.label}>Views</div>
        <div className={styles.value}>{viewsLabel}</div>
      </div> */}
    </div>
  );
};

export default ChannelAboutModal;
